import { Component, ErrorInfo, ReactNode } from 'react'
import styles from './ErrorWrapper.module.css'

interface ErrorWrapperProps {
  children: ReactNode
  /** Section name shown in the error message */
  section?: string
}

interface ErrorWrapperState {
  hasError: boolean
  error: Error | null
}

/**
 * Section-level error boundary. Wraps individual page sections so a crash
 * in one section (e.g. maps, product list) doesn't take down the whole page.
 *
 * Shows an inline error message with a retry button.
 */
export default class ErrorWrapper extends Component<ErrorWrapperProps, ErrorWrapperState> {
  constructor(props: ErrorWrapperProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorWrapperState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(`ErrorWrapper [${this.props.section ?? 'unknown'}]:`, error, errorInfo)
    // TODO: Send to error tracking service (e.g. Sentry)
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className={styles.errorWrapper}>
          <div className={styles.content}>
            <p className={styles.message}>
              {this.props.section
                ? `Не вдалося завантажити секцію "${this.props.section}"`
                : 'Щось пішло не так'}
            </p>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <pre className={styles.details}>{this.state.error.message}</pre>
            )}
            <button className={styles.retryButton} onClick={this.handleRetry}>
              Спробувати ще раз
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
